import { useTranslation } from '@portals-platform/i18n';
import {
  ComboboxField,
  FormField,
  FormGrid,
  SelectField,
  TextareaField,
  TextField,
  useFormContext,
} from '@portals-platform/ui-forms';
import { useMemo } from 'react';
import { useCaptivePolicies, useSelectableCountryOptions } from '../../../data-access/index.js';
import { resetTemplateSelection } from '../../../lib/new-certificate/defaults.js';
import { STATIC_FIELD_CONFIG as fields } from '../../../lib/new-certificate/fields.js';
import { getPolicyYearOptions } from '../../../lib/new-certificate/policy.js';
import type { FormValues } from '../../../lib/new-certificate/types.js';
import { CertificateSection } from './certificate-section.js';

export function CustomerInformationSection() {
  const { t } = useTranslation();
  const { reset, getValues } = useFormContext<FormValues>();
  const countryOptions = useSelectableCountryOptions();
  const policies = useCaptivePolicies();
  const policyYearOptions = useMemo(() => getPolicyYearOptions(policies), [policies]);

  const resetScope = (field: 'countryCode' | 'policyYear', value: string) => {
    reset(resetTemplateSelection({ ...getValues(), [field]: value }));
  };

  return (
    <CertificateSection
      title={t('certificates.new.customerInformation.title')}
      description={t('certificates.new.customerInformation.subtitle')}
    >
      <FormGrid>
        <FormField
          name="certificateIssuedTo"
          label={t(fields.certificateIssuedTo.labelKey)}
          required
        >
          <TextField
            name="certificateIssuedTo"
            autoComplete="organization"
            placeholder={t(fields.certificateIssuedTo.placeholderKey)}
          />
        </FormField>

        <FormField
          name="customerAddress"
          label={t(fields.customerAddress.labelKey)}
          required
          col={12}
        >
          <TextareaField
            name="customerAddress"
            rows={3}
            placeholder={t(fields.customerAddress.placeholderKey)}
          />
        </FormField>

        <FormField name="countryCode" label={t(fields.countryCode.labelKey)} required>
          <ComboboxField
            name="countryCode"
            options={countryOptions}
            placeholder={t(fields.countryCode.placeholderKey)}
            searchPlaceholder={t('certificates.new.customerInformation.searchCountry')}
            disabled={countryOptions.length === 0}
            onValueChange={(value) => resetScope('countryCode', value ?? '')}
          />
        </FormField>

        <FormField name="policyYear" label={t(fields.policyYear.labelKey)} required>
          <SelectField
            name="policyYear"
            options={policyYearOptions}
            placeholder={t(fields.policyYear.placeholderKey)}
            onChange={(event) => resetScope('policyYear', event.target.value)}
          />
        </FormField>
      </FormGrid>
    </CertificateSection>
  );
}
